<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || providerLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !providerLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('country') }}</label>
          <p class="label-info">{{ $t('country-message') }}</p>
          <v-autocomplete
            v-model="provider.country"
            :items="countries"
            :item-value="'id'"
            :item-text="'description'"
            return-object
            outlined
            :placeholder="$t('country')"
            :rules="requiredRule()"
            disabled
            class="required"
            data-cy="country"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('emirate') }}</label>
          <p class="label-info">{{ $t('emirate-message') }}</p>
          <v-autocomplete
            v-model="provider.emirate"
            :items="emirates"
            :item-value="'id'"
            :item-text="'description'"
            outlined
            :placeholder="$t('emirate')"
            :rules="requiredRule()"
            :disabled="!edit"
            class="required"
            data-cy="emirate"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('city') }}</label>
          <p class="label-info">{{ $t('city-message') }}</p>
          <v-text-field
            v-model="provider.city"
            :placeholder="$t('city')"
            outlined
            :rules="minMaxLengthRule(1, 20)"
            :disabled="!edit"
            data-cy="city"
          ></v-text-field>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('street-name-number') }}</label>
                    <p class="label-info">{{ $t('street-name-number-message') }}</p>
                    <v-text-field
                      v-model="provider.street"
                      :placeholder="$t('street-name-number')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="street"
                    ></v-text-field>
                  </v-col>
                  <v-col v-if="false" cols="12" sm="12" md="6">
                    <label>{{ $t('postal-code') }}</label>
                    <p class="label-info">{{ $t('postal-code-message') }}</p>
                    <v-text-field
                      v-model="provider.postalCode"
                      outlined
                      :placeholder="$t('postal-code')"
                      clearable
                      :disabled="!edit"
                      data-cy="postalCode"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('coordinates') }}</label>
                    <p class="label-info">{{ $t('coordinates-message') }}</p>
                    <v-text-field
                      v-model="provider.coordinates"
                      :placeholder="$t('coordinates')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="coordinates"
                      :rules="coordinatesRules"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('po-box') }}</label>
                    <v-text-field
                      v-model="provider.poBox"
                      :placeholder="$t('po-box')"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="poBox"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('submit') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="edit && !next">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import L from 'leaflet';

export default {
  name: 'ProviderLocationInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -2,
      unsavedChanges: false,
      provider: {
        id: null,
        city: null,
        country: { id: 'AE', description: 'United Arab Emirates' },
        street: null,
        poBox: null,
        postalCode: null,
        emirate: null,
        coordinates: null,
      },
      countries: [],
      emirates: [],
      providerLoading: true,
      coordinatesRules: [
        v => this.validateCoordinates(v) || 'Insert valid coordinates e.g 24.4374032, 54.611653',
      ],
    };
  },
  mounted() {
    this.axios
      .get('/api/v1/countries')
      .then(res => (this.countries = res.data.content))
      .finally(() => {
        this.loadingComponents++;
      });
    this.axios
      .get('/api/v1/emirates')
      .then(res => (this.emirates = res.data.content))
      .finally(() => {
        this.loadingComponents++;
      });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    back() {
      console.log('back!');
      this.$emit('tab', 3);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/location-info', this.provider)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('provider-updated'),
          });
          this.unsavedChanges = false;
          if (this.next) {
            sessionStorage.setItem('firstTime', false);
            setTimeout(() => {
              this.$router.push({
                name: 'Dashboard',
              });
            }, '1000');
          } else {
            this.$emit('tab', 5);
          }
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    getProvider(id) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          this.provider = res.data;
          this.provider.country = { id: 'AE', description: 'United Arab Emirates' };
          this.$nextTick(() => {
            this.unsavedChanges = false;
          });
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    validateCoordinates(v) {
      if (v == null) {
        return true;
      } else {
        const coordinates = v.split(',');
        try {
          let lat = L.latLng(coordinates[0].trim(), coordinates[1].trim());
          return lat != null;
        } catch (e) {
          console.log(e);
          return false;
        }
      }
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      let id = this.$route.params.id;
      if (value === false) {
        if (sessionStorage.getItem('providerId')) {
          this.getProvider(sessionStorage.getItem('providerId'));
        } else if (id != null) {
          this.getProvider(id);
        } else {
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        }
      }
    },
    provider: {
      handler: function(val, oldVal) {
        console.log('[Provider] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
